import type {
  InternalActionSheetButton,
  InternalActionSheetOptions
} from "./types";
import type {
  Placement,
  VerticalOrigin,
  HorizontalOrigin
} from "./DropdownActionSheet.types";
import { LAYOUT } from "./DropdownActionSheet.constants";

export interface MenuGroup {
  section?: string;
  buttons: InternalActionSheetButton[];
  /** Whether this run renders as a multi-select MenuSection - see isToggleOnlyTree. */
  selectable: boolean;
}

/**
 * React Aria derives a row's `menuitemcheckbox` role from its section's
 * selection manager, so a run mixing toggles with plain actions must stay
 * unselectable — otherwise those actions announce a checked state they lack.
 */
export const isToggleOnlyTree = (button: InternalActionSheetButton): boolean =>
  button.type === "toggle" &&
  (button.childrenLayout !== "inline" ||
    !button.children?.length ||
    button.children.every(isToggleOnlyTree));

/** Consecutive buttons sharing a `section` become one MenuSection; toggle runs
 *  are split off from other rows so only they carry checkbox semantics. */
export const buildMenuGroups = (
  buttons: readonly InternalActionSheetButton[]
): MenuGroup[] => {
  const groups: MenuGroup[] = [];
  for (const button of buttons) {
    const section = button.section;
    const selectable = isToggleOnlyTree(button);
    const last = groups[groups.length - 1];
    if (last && last.section === section && last.selectable === selectable) {
      last.buttons.push(button);
    } else {
      groups.push({ section, buttons: [button], selectable });
    }
  }
  return groups;
};

export const flattenButtons = (
  buttons: readonly InternalActionSheetButton[]
): InternalActionSheetButton[] =>
  buttons.flatMap((b) =>
    b.children?.length ? [b, ...flattenButtons(b.children)] : [b]
  );

export interface ActionSheetRenderRow {
  button: InternalActionSheetButton;
  /** Indent level for styling, not the true nesting depth — see MAX_ROW_DEPTH. */
  depth: number;
}

/** Styles only key off data-depth="1", so deeper nesting reuses that level
 *  rather than unstyled root spacing. Add depth-N styling before raising. */
const MAX_ROW_DEPTH = 1;

/** A toggle parent collapses its inline children away while switched off. */
const showsInlineChildren = (button: InternalActionSheetButton): boolean =>
  button.type === "toggle" ? button.toggled === true : true;

/** Inline children become sibling rows; submenu-layout buttons are emitted
 *  untouched, children included, for the SubmenuTrigger path to consume. */
export const expandInlineRows = (
  buttons: readonly InternalActionSheetButton[],
  depth = 0
): ActionSheetRenderRow[] =>
  buttons.flatMap((button) => {
    const row: ActionSheetRenderRow = {
      button,
      depth: Math.min(depth, MAX_ROW_DEPTH)
    };
    if (
      button.children?.length &&
      button.childrenLayout === "inline" &&
      showsInlineChildren(button)
    ) {
      return [row, ...expandInlineRows(button.children, depth + 1)];
    }
    return [row];
  });

export interface PlacementResult {
  placement: Placement;
}

export function calculatePlacement(
  triggerPosition: { x: number; y: number },
  options: InternalActionSheetOptions<readonly InternalActionSheetButton[]>
): PlacementResult {
  const estimatedHeaderHeight =
    options.title || options.message ? LAYOUT.HEADER_HEIGHT : 0;
  const estimatedMenuHeight = options.buttons.length * LAYOUT.ITEM_HEIGHT;
  const estimatedTotalHeight = estimatedHeaderHeight + estimatedMenuHeight;

  const spaceBelow = window.innerHeight - triggerPosition.y;
  const spaceAbove = triggerPosition.y;
  const spaceRight = window.innerWidth - triggerPosition.x;
  const spaceLeft = triggerPosition.x;

  const vertical: VerticalOrigin =
    spaceBelow < estimatedTotalHeight + LAYOUT.BOTTOM_PADDING &&
    spaceAbove > spaceBelow
      ? "top"
      : "bottom";

  const horizontal: HorizontalOrigin =
    spaceRight < LAYOUT.MENU_WIDTH + LAYOUT.VIEWPORT_MARGIN &&
    spaceLeft > spaceRight
      ? "right"
      : "left";

  const placement: Placement = `${vertical} ${horizontal === "left" ? "start" : "end"}`;

  return { placement };
}

export function calculateHighlightPosition(
  itemElement: HTMLElement | null,
  menuElement: HTMLElement | null
): { top: number; height: number } | null {
  if (!itemElement || !menuElement) {
    return null;
  }

  const menuRect = menuElement.getBoundingClientRect();
  const itemRect = itemElement.getBoundingClientRect();

  // The rects are viewport-relative, so their delta is only the item's visible
  // offset; FloatingHighlight scrolls with the menu content, so scrollTop must
  // be added or the highlight drifts upward once the menu is scrolled.
  let top = itemRect.top - menuRect.top + menuElement.scrollTop;
  let { height } = itemRect;

  if (itemElement.dataset.divider === "true") {
    const style = getComputedStyle(itemElement);
    const offset =
      parseFloat(style.borderTopWidth) +
      parseFloat(style.paddingTop) -
      parseFloat(style.paddingBottom);
    top += offset;
    height -= offset;
  }

  return { top, height };
}
